import { StoreState } from './store';
import { ShipmentInput, ShipmentSummary, ValidationError, ContainerFitResult, PalletFitResult, FreightCostResult, SavedCalculation } from '@freight-cbm/shared-types';
import { calcShipmentSummary } from '@freight-cbm/shared-logic';
import { validateShipment } from '@freight-cbm/shared-logic';
import { calcContainerFit } from '@freight-cbm/shared-logic';
import { calcPalletFit } from '@freight-cbm/shared-logic';
import { calcFreightCost, calcAirVsOceanComparison, calcFCLCost } from '@freight-cbm/shared-logic';
import { calcBreakEvenDensity, classifyFreightClass } from '@freight-cbm/shared-logic';
import { convertToKg } from '@freight-cbm/shared-logic';
import { CONTAINER_SPECS } from '@freight-cbm/shared-logic';
import { PALLET_SPECS } from '@freight-cbm/shared-logic';

// Derived selectors
export function selectShipmentInput(state: StoreState): ShipmentInput {
  return {
    packages: state.packages,
    mode: state.mode,
    dimDivisor: state.dimDivisor,
    overrideDivisor: state.divisorOverridden,
  };
}

let cachedShipmentInput: string = '';
let cachedSummary: ShipmentSummary | null = null;

export function selectShipmentSummary(state: StoreState): ShipmentSummary {
  const input = selectShipmentInput(state);
  const key = JSON.stringify(input);
  if (key === cachedShipmentInput && cachedSummary) {
    return cachedSummary;
  }
  cachedShipmentInput = key;
  cachedSummary = calcShipmentSummary(input);
  return cachedSummary;
}

export function selectValidationErrors(state: StoreState): ValidationError[] {
  return validateShipment(state.packages, state.dimDivisor);
}

export function selectTotalPackageCount(state: StoreState): number {
  return state.packages.reduce((sum, p) => sum + p.quantity, 0);
}

let cachedContainerKey: string = '';
let cachedContainerResult: ContainerFitResult[] | null = null;

export function selectContainerFits(state: StoreState): ContainerFitResult[] {
  const summary = selectShipmentSummary(state);
  const key = `${summary.totalCBM}:${summary.grossWeightKg}`;
  if (key === cachedContainerKey && cachedContainerResult) {
    return cachedContainerResult;
  }
  cachedContainerKey = key;
  cachedContainerResult = calcContainerFit(summary.totalCBM, summary.grossWeightKg, CONTAINER_SPECS);
  return cachedContainerResult;
}

let cachedPalletKey: string = '';
let cachedPalletResult: PalletFitResult[] | null = null;

export function selectPalletFits(state: StoreState): PalletFitResult[] {
  const firstPkg = state.packages[0];
  if (!firstPkg) return [];
  const key = `${firstPkg.length}:${firstPkg.width}:${firstPkg.height}:${firstPkg.dimUnit}`;
  if (key === cachedPalletKey && cachedPalletResult) {
    return cachedPalletResult;
  }
  cachedPalletKey = key;
  cachedPalletResult = PALLET_SPECS.map((pallet) =>
    calcPalletFit(firstPkg.length, firstPkg.width, firstPkg.height, firstPkg.dimUnit, pallet)
  );
  return cachedPalletResult;
}

export interface PalletLoadResult extends PalletFitResult {
  totalShipmentCartons: number;
  weightLimitedBy: 'height' | 'weight';
}

let cachedPalletLoadKey: string = '';
let cachedPalletLoadResult: PalletLoadResult[] | null = null;

export function selectPalletLoad(state: StoreState): PalletLoadResult[] {
  const firstPkg = state.packages[0];
  if (!firstPkg) return [];

  const baseFits = selectPalletFits(state);
  // totalShipmentCartons uses the first package line's quantity, matching the
  // existing selectPalletFits first-line assumption.
  const totalShipmentCartons = firstPkg.quantity ?? 0;
  const cartonWeightKg = convertToKg(firstPkg.weight, firstPkg.weightUnit);

  const key = `${firstPkg.length}:${firstPkg.width}:${firstPkg.height}:${firstPkg.dimUnit}:${firstPkg.weight}:${firstPkg.weightUnit}:${totalShipmentCartons}`;
  if (key === cachedPalletLoadKey && cachedPalletLoadResult) {
    return cachedPalletLoadResult;
  }
  cachedPalletLoadKey = key;

  cachedPalletLoadResult = baseFits.map((fit, idx) => {
    const pallet = PALLET_SPECS[idx];
    if (!pallet) {
      return {
        ...fit,
        totalShipmentCartons,
        weightLimitedBy: 'height' as const,
      };
    }
    const weightLimitedLayers =
      fit.cartonsPerLayer > 0 && cartonWeightKg > 0
        ? Math.floor(pallet.maxWeightKg / (cartonWeightKg * fit.cartonsPerLayer))
        : Infinity;
    const effectiveLayers = Math.min(fit.layers, weightLimitedLayers);
    const effectiveTotalCartons = fit.cartonsPerLayer * effectiveLayers;
    const palletCount =
      effectiveTotalCartons > 0
        ? Math.ceil(totalShipmentCartons / effectiveTotalCartons)
        : 0;
    const weightLimitedBy: 'height' | 'weight' =
      weightLimitedLayers < fit.layers ? 'weight' : 'height';

    return {
      ...fit,
      layers: effectiveLayers,
      totalCartons: effectiveTotalCartons,
      palletCount,
      totalShipmentCartons,
      weightLimitedBy,
    };
  });
  return cachedPalletLoadResult;
}

let cachedCostKey: string = '';
let cachedCostResult: FreightCostResult | null = null;

export function selectFreightCost(state: StoreState): FreightCostResult {
  const summary = selectShipmentSummary(state);
  const key = `${summary.chargeableWeightKg}:${summary.totalCBM}:${state.costInput.ratePerKg}:${state.costInput.ratePerCBM}:${state.costInput.ratePerRevenueTon}:${state.costInput.ratePerContainer}:${state.costInput.currency}:${state.mode}:${state.costInput.accessorials.map(a => `${a.name}:${a.cost}`).join(',')}`;
  if (key === cachedCostKey && cachedCostResult) {
    return cachedCostResult;
  }
  cachedCostKey = key;
  cachedCostResult = calcFreightCost(summary, state.costInput, state.mode);
  return cachedCostResult;
}

export function selectFCLCost(state: StoreState): FreightCostResult | null {
  if (state.costInput.ratePerContainer <= 0) return null;
  const containerCount = selectContainerFits(state)[0]?.containerCount ?? 0;
  return calcFCLCost(
    containerCount,
    state.costInput.ratePerContainer,
    state.costInput.accessorials,
    state.costInput.currency,
  );
}

let cachedComparisonKey: string = '';
let cachedComparisonResult: { airCost: FreightCostResult; oceanCost: FreightCostResult; savings: number } | null = null;

export function selectAirVsOceanComparison(state: StoreState): {
  airCost: FreightCostResult;
  oceanCost: FreightCostResult;
  savings: number;
} {
  const input = selectShipmentInput(state);
  const key = `${input.packages.map(p => `${p.length}:${p.width}:${p.height}:${p.weight}:${p.quantity}`).join('|')}:${state.costInput.ratePerKg}:${state.costInput.ratePerCBM}:${state.costInput.currency}`;
  if (key === cachedComparisonKey && cachedComparisonResult) {
    return cachedComparisonResult;
  }
  cachedComparisonKey = key;
  cachedComparisonResult = calcAirVsOceanComparison(input, state.costInput.ratePerKg, state.costInput.ratePerCBM, state.costInput.currency);
  return cachedComparisonResult;
}

export function selectCargoDensity(state: StoreState): number {
  const summary = selectShipmentSummary(state);
  return summary.cargoDensityKgPerCBM;
}

export function selectBreakEvenDensity(state: StoreState): number {
  return calcBreakEvenDensity(state.dimDivisor);
}

export function selectFreightClass(state: StoreState): string {
  const density = selectCargoDensity(state);
  return classifyFreightClass(density);
}

let cachedHistoryKey: string = '';
let cachedHistoryResult: SavedCalculation[] | null = null;

export function selectFilteredHistory(state: StoreState) {
  const query = state.searchQuery.toLowerCase();
  const key = `${query}:${state.calculations.length}:${state.calculations[0]?.id ?? ''}`;
  if (key === cachedHistoryKey && cachedHistoryResult) {
    return cachedHistoryResult;
  }
  cachedHistoryKey = key;
  if (!query) {
    cachedHistoryResult = state.calculations;
    return cachedHistoryResult;
  }
  cachedHistoryResult = state.calculations.filter(
    (c) =>
      c.name.toLowerCase().includes(query) ||
      c.timestamp.includes(query)
  );
  return cachedHistoryResult;
}


